import { ActorRef, Typestate } from 'xstate';

export interface Choice {
  answerTypeString?: string;
  index: number;
  text: string;
  mappingValue: string;
  next: { male: { nextQuestionId: string | null } };
}

export enum QuestionType {
  singleSelect = 'SingleSelect',
  input = 'Input',
  multiSelect = 'MultiSelect',
  text = 'text',
}

interface Interstitial {
  header: string;
  ctaText: string;
  content: string;
  disclaimer: string;
  avatar: { image_url: string; name: string; title: string };
}

export interface Question {
  isOptional?: boolean;
  html?: string;
  id: string;
  key: string;
  text: string;
  subtitle: string | null;
  tooltip: string | null;
  progressIndicatorPct: number;
  questionTypeString: QuestionType;
  interstitial?: Interstitial;
  choices: Choice[];
}

export interface UserAnswers {
  [key: string]: string | number;
}

export interface QuestionnaireContext {
  selectedQuestionId?: string | null;
  currentQuestion?: Question;
  answers: UserAnswers;
  questions: Question[];
  previousQuestions: Question[];
}

export interface ProductRecommenderContext {
  firstQuestionId?: string;
  answers: UserAnswers;
  questions: Question[];
}

export enum QuestionnaireEvent {
  ANSWERED = 'ANSWERED',
  BACK = 'BACK',
  RETRY = 'RETRY',
}

export interface SelectedChoice extends Choice {
  selectedValue?: string | number;
}

export type QuestionnaireEventType = {
  ignoreChoices?: boolean;
  key: string;
  type: QuestionnaireEvent;
  choices?: SelectedChoice | SelectedChoice[];
};

export enum QuestionnaireStates {
  userInput = 'userInput',
  loadQuestion = 'loadQuestion',
  allQuestionsAnswered = 'allQuestionsAnswered',
}

export type QuestionnaireStateSchema = {
  value: QuestionnaireStates;
  context: QuestionnaireContext;
};

export enum ProductRecommenderStates {
  loadingQuestions = 'loadingQuestions',
  questionsLoaded = 'questionsLoaded',
  submitQuestionnaire = 'submitQuestionnaire',
  failed = 'failed',
  done = 'done',
}

export type ProductRecommenderStateSchema = {
  value: ProductRecommenderStates;
  context: ProductRecommenderContext;
};

export type QuestionnaireActorType = ActorRef<QuestionnaireEventType, Typestate<QuestionnaireContext>>;

export interface QuestionnaireMachineEvent {
  data: UserAnswers;
}

export enum MachineId {
  productRecommender = 'productRecommender',
  getQuestions = 'getQuestions',
  generalHealth = 'generalHealth',
  submitQuestionnaire = 'submitQuestionnaire',
}

interface Product {
  id: string;
  marketId: string;
  title: string;
  content: string;
  price: string;
  icon: string;
  seoUrl: string;
}

export interface RecommendationsData {
  gender: string;
  age: string;
  healthGoal: string;
  discount: number;
  products: Product[];
}

export interface QuestionnaireResponse {
  data: {
    firstQuestionId: string;
    questions: Question[];
  };
}
